/**
 * ALOHA PLATFORM - Avatar Chat with Voice
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import UpgradePrompt from '../components/UpgradePrompt';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';
import VoiceAvatarChat from '../components/VoiceAvatarChat';

const AvatarChat = () => {
  const { user, isAuthenticated, token } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState('queen_supernova');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has avatar chat access
    if (user && !['PRO', 'ENTERPRISE', 'LIFETIME_ENTERPRISE'].includes(user.subscription_tier)) {
      setShowUpgrade(true);
    }
    
    // Fetch available avatars
    fetchAvailableAvatars();
  }, [user]);

  const fetchAvailableAvatars = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avatar/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAvailableAvatars(data.avatars || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch avatars:', error);
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (showUpgrade) {
    return <UpgradePrompt currentTier={user?.subscription_tier} onClose={() => setShowUpgrade(false)} />;
  }

  return (
    <div className="min-h-screen relative">
      <RealisticGalaxyBackground />
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="bg-black/40 backdrop-blur-xl rounded-t-2xl p-6 border-b border-white/20">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Aloha Queen Supernova Agent
                </h1>
                <p className="text-gray-300">
                  Talk with AI using voice or text • 3D animated avatar with lip sync • Powered by Gemini Pro
                </p>
              </div>
              
              {/* Avatar Selector */}
              {!loading && availableAvatars.length > 0 && (
                <div className="min-w-[250px]">
                  <label className="block text-gray-300 text-sm mb-2">Select Avatar:</label>
                  <select
                    value={selectedAvatar}
                    onChange={(e) => setSelectedAvatar(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-800/80 border border-purple-500/50 rounded-lg text-white font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  >
                    {availableAvatars.map(avatar => (
                      <option key={avatar.id} value={avatar.id}>
                        {avatar.name} {avatar.exclusive ? '👑' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    {availableAvatars.find(a => a.id === selectedAvatar)?.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Interface */}
          <div className="bg-black/40 backdrop-blur-xl rounded-b-2xl p-6 border-t border-white/10">
            <VoiceAvatarChat 
              token={token}
              sessionId={sessionId}
              onSessionUpdate={setSessionId}
              selectedAvatar={selectedAvatar}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarChat;
